import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import get from 'lodash/get';
import Ranks, { RanksHeaderItem, RanksType } from '../atoms/Ranks';
import Loader from '../atoms/Loader';
import NamedBox from '../atoms/NamedBox';
import PAIChart from '../molecules/PAIChart';
import CombineChart from '../molecules/CombineChart';
import ProCombineChart from '../molecules/ProCombineChart';
import UpdatedPPIChart from '../molecules/UpdatedPPIChart';
import MultiSportChart from '../molecules/MultiSportChart';
import { RanksComparisonGroup } from '../molecules/RanksTooltip';
import { TAB, TABS_VALUES_ENUM } from './UpdatedPlayerProfileStats';
import PlayerCombineData from './PlayerCombineData';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import MEDIA from '../styles/media';
import Position from '../types/Position';
import Team from '../types/Team';
import Player from '../types/Player';
import College from '../types/College';
import HighSchool from '../types/HighSchool';
import PlayerPAIStats from '../types/PlayerPAIStats';
import PlayerNFLPAIStats from '../types/PlayerNFLPAIStats';
import PlayerCombineStats from '../types/PlayerCombineStats';
import PlayerCombineEvent from '../types/PlayerCombineEvent';
import PlayerNFLCombineEvent from '../types/PlayerNFLCombineEvent';
import PlayerProCombineStats from '../types/PlayerProCombineStats';
import PlayerPPIStats, { UpdatedPlayerPPIStat } from '../types/PlayerPPIStats';
import PlayerNFLPPIStats from '../types/PlayerNFLPPIStats';
import { TEAM_TYPE } from '../templates/PlayerPageTemplate';
import { formatDate } from '../services/player';
import TabPanel from '../molecules/TabPanel';
import PPIChartsData from '../molecules/PPIChartsData';
import Column from '../types/Column';
import { CHARTS_COLUMN } from '../types/ChartsColumn';
import Rank from '../types/Rank';
import ProfileStatsCategoriesModal from './ProfileStatsCategoriesModal';
import { PlayerEventStatsComparison } from '../types/StatsPlayer';
import { COLOR_BLUE } from '../styles/colors';
import {
  ProfileStatsCategory,
  PROFILE_STATS_CATEGORY,
  getCollegeHeaderItemByColumn,
  getNflHeaderItemByColumn,
  getProfileStatsComparisonRankValue,
} from '../services/profile-stats-categories';
import { State } from '../redux/reducers';
interface UpdatedPlayerChartsProps {
  className?: string;
  loading: boolean;
  selectedTab: number;
  player?: Player;
  collegeTeam?: Team;
  nflTeam?: Team;
  position?: Position;
  isNFL: boolean;
  hideTabs?: boolean;
  paiStats?: PlayerEventStatsComparison | PlayerPAIStats | PlayerNFLPAIStats;
  combineStats?: PlayerEventStatsComparison | PlayerCombineStats | PlayerProCombineStats;
  combineEvents: Array<PlayerCombineEvent | PlayerNFLCombineEvent>;
  ppiStats?: UpdatedPlayerPPIStat;
  hasPaiData?: boolean;
  hasCombineScore?: boolean;
  hasProCombineData?: boolean;
  hasHSCombineData?: boolean;
  hasPPIScore?: boolean;
  printed?: boolean;
  onTabChange: (tab:number) => void;
  selectedTeamType?: string;
  setSelectedTabValue?: React.Dispatch<React.SetStateAction<string>>,
  selectedTabValue?: string,
  columns?: Column[];
  profileStatsCategoriesOptions: ProfileStatsCategory[];
  profileStatsCategories: ProfileStatsCategory[];
  onUpdateProfileStatsCategories: (values:ProfileStatsCategory[]) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  width: {
    width: '500px',
  },
  playerCharts: {
    position: 'relative',
  },
  playerChartsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

    '& .MuiTab-root': {
      minHeight: 0,
    },
  },
  playerChartsHeaderName: {
    position: 'absolute',
    bottom: -40,
    left: 20,
  },

  printed: {
    '& $playerChartsHeaderName': {
      position: 'static',
    },
  },

  tabs: {
    position: 'absolute',
    bottom: 0,
  },
  tabsIndicator: {
    height: '3px',
  },
  tab: {
    minWidth: 'auto',
    minHeight: '40px',
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(17),
    lineHeight: 1.2,
    padding: theme.spacing(2, 1.5),
    textTransform: 'none',
    opacity: 1,
  },

  collapsedTab: {
    width: 0,
    padding: 0,
    visibility: 'hidden',
  },

  chart: {
    width: '100%',
    height: '280px',
  },
  chartContainer: {
    minHeight: '200px',
  },
  hsCombineData: {
    minHeight: '200px',
  },
  addRemoveColumns: {
    margin: theme.spacing(0, 4, 1.5, 4),
    padding: 0,
    textDecoration: 'underline',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: COLOR_BLUE,
  },

  '@media (max-width: 1260px)': {
    tabs: {
      position: 'relative',
      marginTop: theme.spacing(1),
    },

    playerChartsHeader: {
      height: 'auto',
      display: 'block',
      padding: theme.spacing(0, 0, 0, 1.25),
    },
  },

  [MEDIA.MOBILE]: {
    tab: {
      minHeight: 0,
      padding: theme.spacing(1),
    },
  },

  [MEDIA.DESKTOP_LARGE]: {
    tab: {
      // padding: theme.spacing(2),
    },
  },
  selectedTab: {
    fontWeight: 600
  },
}), { name: UpdatedPlayerCharts.name });

export default function UpdatedPlayerCharts (props:UpdatedPlayerChartsProps) {
  const {
    className,
    loading,
    selectedTab = TAB.PAI,
    player,
    collegeTeam,
    nflTeam,
    position,
    isNFL,
    paiStats,
    combineStats,
    combineEvents,
    ppiStats,
    hideTabs = false,
    hasPaiData = false,
    hasHSCombineData = false,
    hasCombineScore = false,
    hasPPIScore = false,
    hasProCombineData = false,
    columns = [],
    profileStatsCategoriesOptions,
    profileStatsCategories,
    printed = false,
    onTabChange,
    selectedTeamType,
    selectedTabValue,
    setSelectedTabValue,
    onUpdateProfileStatsCategories,
  } = props;
  const classes = useStyles();
  const newProfileEnabled = useSelector((state: State) => Boolean(state.user?.program?.newProfileEnabled));
  const [isProfileStatsModalOpen, setIsProfileStatsModalOpen] = React.useState<boolean>(false);

  const [headersList, setHeadersList] = React.useState<RanksHeaderItem[]>([]);
  const [dataValues, setDataValues] = React.useState<Rank[]>([]);

  const college = ((player && player.playerColleges) || [])
    .find((college:College) => college && college.isPrimary);
  const highSchool = ((player && player.highSchools) || [])
    .find((highSchool:HighSchool) => highSchool && highSchool.isPrimary);
  const recruitingClass = (highSchool && highSchool.graduatingClass) || (college && college.recruitingClass);

  const currentStats = (selectedTabValue === TABS_VALUES_ENUM.PAI && paiStats)
    || ((selectedTabValue === TABS_VALUES_ENUM.COMBINE || selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE) && combineStats)
    || (selectedTabValue === TABS_VALUES_ENUM.PPI && ppiStats);
    // || (selectedTabValue === TABS_VALUES_ENUM.PPI && ppiStats);

  const isProCombineTabButNoScore = selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE && !player?.nflCombineScore;

  let ranksType = selectedTabValue === TABS_VALUES_ENUM.PAI ? RanksType.PAI : RanksType.COMBINE;
  let comparisonEvent = selectedTabValue === TABS_VALUES_ENUM.PAI ? 'pai' : 'combine';
  if (selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE) {
    ranksType = RanksType.PRO_COMBINE;
    comparisonEvent = 'proCombine';
  } else if (selectedTabValue === TABS_VALUES_ENUM.PPI) {
    ranksType = RanksType.PPI;
    comparisonEvent = 'ppi';
  }

  const renderTabs = () => {
    let tabsList = [
      { label: 'PAI', value: TABS_VALUES_ENUM.PAI, disabled: !hasPaiData },
      { label: 'HS Combine', value: TABS_VALUES_ENUM.COMBINE, disabled: !(hasCombineScore || hasHSCombineData) },
      { label: 'HS Sports', value: TABS_VALUES_ENUM.MULTISPORT, disabled: false },
    ]
    switch(selectedTeamType) {
      case TEAM_TYPE.HIGH_SCHOOL:
        tabsList = [...tabsList];
        break;
      case TEAM_TYPE.COLLEGE:
        tabsList = [
          { label: 'PPI', value: TABS_VALUES_ENUM.PPI, disabled: !hasPPIScore },
          { label: 'PAI', value: TABS_VALUES_ENUM.PAI, disabled: !hasPaiData },
          { label: 'HS Combine', value: TABS_VALUES_ENUM.COMBINE, disabled: !(hasCombineScore || hasHSCombineData) },
          { label: 'HS Sports', value: TABS_VALUES_ENUM.MULTISPORT, disabled: false },
          { label: 'Pro Combine', value: TABS_VALUES_ENUM.PRO_COMBINE, disabled: !hasProCombineData },
        ];
        break;
      case TEAM_TYPE.NFL:
        tabsList = [
          { label: 'Pro Combine', value: TABS_VALUES_ENUM.PRO_COMBINE, disabled: !hasProCombineData },
          { label: 'PPI', value: TABS_VALUES_ENUM.PPI, disabled: !hasPPIScore },
          { label: 'PAI', value: TABS_VALUES_ENUM.PAI, disabled: !hasPaiData },
          { label: 'HS Combine', value: TABS_VALUES_ENUM.COMBINE, disabled: !(hasCombineScore || hasHSCombineData) },
          { label: 'HS Sports', value: TABS_VALUES_ENUM.MULTISPORT, disabled: false },
        ];
        break;
    }
    return (
      <TabPanel 
        tabsList={tabsList}
        selectedTab={selectedTabValue} 
        setSelectedTab={setSelectedTabValue} 
        hideEvaluation={true}
      />
    )
  }

  const formatProfileStatsCategoryLabel = (value:string) => {
      const labelsByCategory: Record<string, string> = {
      [PROFILE_STATS_CATEGORY.HS_CLASS]: `HS Class (${recruitingClass})`,
      [PROFILE_STATS_CATEGORY.DIVISION_1]: 'Division I (Since 2010)',
      [PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM]: `${collegeTeam?.name} (Since 2010)` || 'Team Select Team',
      [PROFILE_STATS_CATEGORY.TEAM_SELECT_CONFERENCE]: `${collegeTeam?.conference?.name} (Since 2010)` || 'Team Select Conference',
      [PROFILE_STATS_CATEGORY.NFL_DRAFT]: 'NFL Draft (Since 2010)',
      [PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM_NFL]: `${nflTeam?.name} (Since 2010)` || 'Team Select Team NFL',
      [PROFILE_STATS_CATEGORY.NFL_UDFA]: 'NFL UDFA (Since 2010)',
      [PROFILE_STATS_CATEGORY.TRANSFER_PORTAL]: 'Transfer Portal (Active)',
      [PROFILE_STATS_CATEGORY.TEAM_SELECT_STARTER]: 'Team Starter (Since 2015)',
      [PROFILE_STATS_CATEGORY.CONFERENCE_STARTER]: 'Conference Select Starter (Since 2015)',
      [PROFILE_STATS_CATEGORY.ALL_CONFERENCE]: 'All-Conference (Since 2010)',
      [PROFILE_STATS_CATEGORY.ALL_AMERICAN]: 'All-American (Since 2010)',
      [PROFILE_STATS_CATEGORY.PRO_BOWL]: 'Pro Bowl (Since 2010)',
      [PROFILE_STATS_CATEGORY.ALL_PRO]: 'All-Pro (Since 2010)',
    };

    return labelsByCategory[value]
      || value
        .split('-')
        .filter(Boolean)
        .map((part:string) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
  };

  const renderHeadersList = () => {
    const headersList: RanksHeaderItem[] = [];
    const values: Rank[] = [];
    const comparisonStats = get(currentStats, 'comparisonStats', []);
    

    columns.forEach((column:Column, index: number) => {
      if (column.selected) {
        const rankValue = getProfileStatsComparisonRankValue({
          column,
          columnIndex: index,
          comparisonStats,
          isNFL,
          newProfileEnabled,
        });

        const headerItem = isNFL
          ? getNflHeaderItemByColumn(column, {
            collegeTeam,
            nflTeam,
          })
          : getCollegeHeaderItemByColumn(column, {
            recruitingClass,
            collegeTeam,
          });

        headersList.push(headerItem);
        values.push(rankValue);
      }
    });

    setDataValues(values);
    setHeadersList(headersList);
  }

  useEffect(() => {
    renderHeadersList();
  }, [columns, selectedTabValue, currentStats, isNFL, collegeTeam, nflTeam, recruitingClass]);

  return (
    <NamedBox
      className={clsx(classes.playerCharts, printed && classes.printed, className)}
      // headerClassName={clsx(classes.playerChartsHeader, selectedTeamType === TEAM_TYPE.HIGH_SCHOOL && classes.justifyEnd)}
      headerClassName={classes.playerChartsHeader}
      headerNameClassName={classes.playerChartsHeaderName}
      name={`
        ${selectedTabValue === TABS_VALUES_ENUM.PAI ? 'Player Athletic Index®' : ''}
        ${selectedTabValue === TABS_VALUES_ENUM.COMBINE ? (hasCombineScore ? 'HS Combine Score' : (hasHSCombineData ? 'HS Combine Data' : '')) : ''}
        ${selectedTabValue === TABS_VALUES_ENUM.PPI ? '(PPI) College Production Score' : ''}
        ${selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE ? (hasProCombineData ? 'Pro Combine Score' :  '') : ''}
        ${selectedTabValue === TABS_VALUES_ENUM.MULTISPORT ? 'HS Sports' : ''} - ${position || ''}
      `}
      subHeader={`${selectedTabValue === TABS_VALUES_ENUM.PPI && player?.ppiDate ? `Updated: ${formatDate(player?.ppiDate, true)} `: ''}`}
      printed={printed}
      headerContent={!hideTabs ? (
        renderTabs()
      ) : null}
    >
      <Loader inProgress={loading} />

      {!isProCombineTabButNoScore && (
        <div className={classes.chartContainer}>
          {selectedTabValue === TABS_VALUES_ENUM.PAI && (
            <PAIChart
              className={classes.chart}
              team={isNFL ? nflTeam : collegeTeam}
              player={player}
              position={position}
            />
          )}

          {(selectedTabValue === TABS_VALUES_ENUM.COMBINE && hasCombineScore) && (
            <CombineChart
              className={classes.chart}
              team={isNFL ? nflTeam : collegeTeam}
              player={player}
              position={position}
            />
          )}

          {(selectedTabValue === TABS_VALUES_ENUM.PPI && hasPPIScore) && (
            <div id="ppi">
              <UpdatedPPIChart
                className={classes.chart}
                team={isNFL ? nflTeam : collegeTeam}
                player={player}
                position={position}
              />
            </div>
          )}

          {(selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE && hasProCombineData) && (
            <ProCombineChart
              className={classes.chart}
              team={isNFL ? nflTeam : collegeTeam}
              player={player}
              position={position}
              isNFL={isNFL}
            />
          )}

          {((selectedTabValue === TABS_VALUES_ENUM.COMBINE) && !hasCombineScore && (hasHSCombineData || hasProCombineData)) && (
            <PlayerCombineData
              className={classes.hsCombineData}
              combineEvents={combineEvents}
              player={player}
              collegeTeam={collegeTeam}
              nflTeam={nflTeam}
              position={position}
              isNFL={isNFL}
              hideHeader
            />
          )}

          {selectedTabValue === TABS_VALUES_ENUM.MULTISPORT && (
            <MultiSportChart
              sports={player?.multiSport || []}
            />
          )}
        </div>
      )}

      {
        (selectedTabValue === TABS_VALUES_ENUM.PPI && hasPPIScore) && (
          <>  
            <ButtonBase
              className={classes.addRemoveColumns}
              onClick={() => setIsProfileStatsModalOpen(true)}
            >
              Add / Remove Columns
            </ButtonBase>
            <PPIChartsData 
              player={player}
              collegeTeam={collegeTeam}
              nflTeam={nflTeam}
              position={position}
              className={classes.ppiChartsData}
              isNFL={isNFL}
              ppiStats={ppiStats}
              columns={columns}
              comparisonEvent={comparisonEvent}
            />
          </>
        )
      }

      {/* Combine and Pro combine  */}
      {((selectedTabValue !== TABS_VALUES_ENUM.COMBINE && selectedTabValue !== TABS_VALUES_ENUM.PRO_COMBINE 
      && selectedTabValue !== TABS_VALUES_ENUM.PPI)
        || (selectedTabValue === TABS_VALUES_ENUM.COMBINE && hasCombineScore)
        || (selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE && hasProCombineData && !isProCombineTabButNoScore)
      ) && (
        <>
          <ButtonBase
            className={classes.addRemoveColumns}
            onClick={() => setIsProfileStatsModalOpen(true)}
          >
            Add / Remove Columns
          </ButtonBase>
          {(currentStats && collegeTeam && !isNFL) && (
            <Ranks
              type={ranksType}
              playerId={player?.id}
              teamId={collegeTeam?.id}
              collegeTeamId={collegeTeam?.id}
              nflTeamId={nflTeam?.id}
              position={position}
              comparisonEvent={comparisonEvent}
              combineId={null}
              combineCategory={null}
              headerItems={headersList.length ? headersList : []}
              values={dataValues}
              bottomSpace={selectedTabValue !== TABS_VALUES_ENUM.PPI}
              isNFL={isNFL}
            />
          )}
          {/* NFL is on */}
          {(currentStats && collegeTeam && nflTeam && isNFL) && (
            <Ranks
              type={ranksType}
              playerId={player?.id}
              teamId={collegeTeam?.id}
              collegeTeamId={collegeTeam?.id}
              nflTeamId={nflTeam?.id}
              position={position}
              comparisonEvent={comparisonEvent}
              combineId={null}
              combineCategory={null}
              headerItems={headersList}
              values={dataValues}
              isNFL={isNFL}
              bottomSpace={selectedTabValue !== TABS_VALUES_ENUM.PPI}
            />
          )}
        </>
      )}

      <ProfileStatsCategoriesModal
        open={isProfileStatsModalOpen}
        onClose={() => setIsProfileStatsModalOpen(false)}
        options={profileStatsCategoriesOptions}
        values={profileStatsCategories}
        isNFL={isNFL}
        onChange={onUpdateProfileStatsCategories}
        getOptionLabel={formatProfileStatsCategoryLabel}
      />
    </NamedBox>
  );
}
